/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ThemedButton from '../components/common/ThemedButton';
import { inlineButton } from '../components/athlohive/EventFormFields';
import { CERTIFICATE_TEMPLATE_TABLE, afterMinimumLoader, deleteTemplateFile } from '../components/athlohive/certificateTemplates';
import {
  Notice, PreviewModal, apiHelper, templatesKey, useApplication, useTemplateUsage, useTemplates,
} from '../components/athlohive/CertificateTemplateParts';

/**
 * Certificate Templates — the list the organizer picks a certificate design
 * from (certificate_template). Preview, Edit (its own page), Delete, and Add
 * template (its own page). The HTML file lives on file storage; its link is
 * the row's Template URL.
 *
 * Delete removes the row, then its file from file storage. A template an event
 * activity uses cannot be deleted — the database refuses it (ON DELETE
 * RESTRICT), and issued certificates are drawn from that file every time they
 * are viewed — so it says so and suggests making it Inactive instead.
 */
export default function CertificateTemplatesPage() {
  const { id: applicationId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();
  const queryClient = useQueryClient();
  const { application, organizationId } = useApplication(applicationId);
  const templates = useTemplates(applicationId, organizationId);
  const usage = useTemplateUsage(applicationId, organizationId);

  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const listPath = `/dashboard/applications/${applicationId}/certificate-templates`;
  const uses = (row) => (usage.data || {})[String(row.id)] || 0;
  const deletingRow = deleting === null ? null : (templates.data || []).find((row) => row.id === deleting) || { name: '' };

  const remove = async (row) => {
    setDeleting(row.id);
    const startedAt = Date.now();
    try {
      const resp = await apiHelper.apiCall('/arcee/application/table/delete-record/', {
        method: 'DELETE',
        body: JSON.stringify({
          table_name: CERTIFICATE_TEMPLATE_TABLE,
          organization_id: organizationId,
          application_id: Number(applicationId),
          id: row.id,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || body.detail || `The template could not be deleted (${resp.status}).`);
      }
      // The row is gone; now its file. A file left behind is only clutter, so a failure here is reported, not undone.
      let fileGone = true;
      try {
        fileGone = await deleteTemplateFile(row.template_url, {
          organizationId, applicationId: Number(applicationId),
          fileServerUrl: envConfig.fileServerUrl, apiKey: envConfig.fileServerApiKey,
        });
      } catch (_) {
        fileGone = false;
      }
      await afterMinimumLoader(startedAt);
      queryClient.invalidateQueries({ queryKey: templatesKey(applicationId, organizationId) });
      queryClient.invalidateQueries({ queryKey: ['certificateTemplateUsage', applicationId, organizationId] });
      showAlertDialog({
        title: 'Template deleted',
        message: fileGone
          ? `“${row.name}” and its file were deleted.`
          : `“${row.name}” was deleted, but its file could not be removed from file storage. Remove it in the File Manager.`,
        type: fileGone ? 'success' : 'warning', okLabel: 'Done', showCancel: false,
      });
    } catch (err) {
      await afterMinimumLoader(startedAt);
      showAlertDialog({ title: 'Could not delete', message: err.message, type: 'error', okLabel: 'OK', showCancel: false });
    }
    setDeleting(null);
  };

  const confirmDelete = (row) => {
    const n = uses(row);
    if (n > 0) {
      showAlertDialog({
        title: 'This template is in use',
        message: `“${row.name}” is chosen for ${n} event ${n === 1 ? 'activity' : 'activities'}, and certificates `
          + 'issued with it are drawn from its file. It cannot be deleted. To stop organizers picking it, edit it '
          + 'and turn off Active.',
        type: 'warning', okLabel: 'OK', showCancel: false,
      });
      return;
    }
    showAlertDialog({
      title: 'Delete this template?',
      message: `“${row.name}” will be deleted, and its HTML file removed from file storage. This cannot be undone.`,
      type: 'warning', okLabel: 'Delete', cancelLabel: 'Cancel', showCancel: true,
      onOk: () => remove(row),
    });
  };

  const cell = { padding: '10px 12px', borderBottom: '1px solid #eef0f3', fontSize: 14, verticalAlign: 'middle' };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/applications/${applicationId}`)}>
          {application?.name || 'Application'}
        </span>
        {' / Certificate Templates'}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h2 style={{ color: theme.primary, margin: 0 }}>Certificate Templates</h2>
        {templates.isSuccess && (
          <ThemedButton type="button" style={inlineButton} onClick={() => navigate(`${listPath}/new`)}>Add template</ThemedButton>
        )}
      </div>

      <Notice>
        Organizers pick one of the <strong>active</strong> templates for each activity of their event.
        A template that an activity uses cannot be deleted; make it inactive instead.
      </Notice>

      {templates.isLoading && <div style={{ padding: 16 }}>Loading…</div>}
      {templates.isError && (
        <Notice tone="warn">
          The certificate templates could not be loaded. If this application has no
          <code> certificate_template</code> table yet, run
          <code> update_athlohive_pack_v3_certificate_tables</code> and apply the pack Update first.
        </Notice>
      )}

      {templates.isSuccess && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
          {templates.data.length === 0 ? (
            <div style={{ padding: 20, color: '#6b7280', fontSize: 14 }}>
              No templates yet. Add the first one with <strong>Add template</strong>.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={cell}>Name</th>
                  <th style={cell}>Description</th>
                  <th style={cell}>Status</th>
                  <th style={cell}>Used by</th>
                  <th style={{ ...cell, width: 1 }} />
                </tr>
              </thead>
              <tbody>
                {templates.data.map((row) => (
                  <tr key={row.id} style={{ opacity: row.is_active === false ? 0.6 : 1 }}>
                    <td style={{ ...cell, fontWeight: 600 }}>{row.name}</td>
                    <td style={{ ...cell, color: '#4b5563' }}>{row.description || '—'}</td>
                    <td style={cell}>
                      <span style={{
                        fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '2px 10px',
                        background: row.is_active === false ? '#e5e7eb' : '#dcfce7',
                        color: row.is_active === false ? '#374151' : '#166534',
                      }}>
                        {row.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td style={{ ...cell, color: '#4b5563', whiteSpace: 'nowrap' }}>
                      {usage.isSuccess ? (uses(row) ? `${uses(row)} ${uses(row) === 1 ? 'activity' : 'activities'}` : '—') : '…'}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-sm btn-outline-secondary me-2" onClick={() => setPreview(row)}>
                        <i className="bi bi-eye" /> Preview
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => navigate(`${listPath}/${row.id}/edit`)}>
                        <i className="bi bi-pencil" /> Edit
                      </button>
                      <button
                        type="button" className="btn btn-sm btn-outline-danger"
                        disabled={deleting !== null || usage.isLoading}
                        title={uses(row) ? `Used by ${uses(row)} event ${uses(row) === 1 ? 'activity' : 'activities'}` : 'Delete this template and its file'}
                        onClick={() => confirmDelete(row)}
                      >
                        {deleting === row.id
                          ? <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                          : <i className="bi bi-trash" />}
                        {' '}{deleting === row.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} />}

      {deletingRow && (
        // Blocks the page while the row and then its file are deleted.
        <div role="alertdialog" aria-modal="true" aria-live="assertive" style={{
          position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(15,23,42,.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '20px 24px', minWidth: 280,
            boxShadow: '0 20px 50px -12px rgba(15,23,42,.35)', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span className="spinner-border" aria-hidden="true" style={{ color: theme.primary }} />
            <div>
              <div style={{ fontWeight: 600 }}>Deleting template…</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>“{deletingRow.name}” and its file on file storage</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
